import MarkdownIt from 'markdown-it';
import { katex } from '@mdit/plugin-katex';
import DOMPurify from 'dompurify';
import { applyHighlightingToContainer } from './code';
import { renderMermaidInContainer } from './diagram';

const md: MarkdownIt = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
    breaks: true
}).use(katex);

const renderCache = new Map<string, DocumentFragment>();

function simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return hash.toString(16);
}

function htmlToFragment(html: string): DocumentFragment {
    const template = document.createElement('template');
    template.innerHTML = DOMPurify.sanitize(html);
    const fragment = template.content;
    
    fragment.querySelectorAll('table').forEach(table => {
        const wrapper = document.createElement('div');
        wrapper.className = 'table-wrapper';
        table.parentNode?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });
    
    return fragment;
}

function fragmentToHtml(fragment: DocumentFragment): string {
    const div = document.createElement('div');
    div.appendChild(fragment.cloneNode(true));
    return div.innerHTML;
}

export async function renderMessage(text: string): Promise<string> {
    const fragment = await renderMessageFragment(text);
    return fragmentToHtml(fragment);
}

export async function renderMessageFragment(text: string): Promise<DocumentFragment> {
    const cacheKey = simpleHash(text);
    
    if (renderCache.has(cacheKey)) {
        return renderCache.get(cacheKey)!.cloneNode(true) as DocumentFragment;
    }
    
    try {
        const html = md.render(text);
        const fragment = htmlToFragment(html);
        
        applyHighlightingToContainer(fragment);
        
        await renderMermaidInContainer(fragment);
        
        renderCache.set(cacheKey, fragment.cloneNode(true) as DocumentFragment);
        return fragment;
    } catch (error) {
        console.error('Error rendering message:', error);
        const fragment = document.createDocumentFragment();
        const textNode = document.createTextNode(text);
        fragment.appendChild(textNode);
        return fragment;
    }
}

export function renderMessageSync(text: string): string {
    const fragment = renderMessageFragmentSync(text);
    return fragmentToHtml(fragment);
}

export function renderMessageFragmentSync(text: string): DocumentFragment {
    try {
        const html = md.render(text);
        const fragment = htmlToFragment(html);
        
        applyHighlightingToContainer(fragment);
        
        return fragment;
    } catch (error) {
        console.error('Error rendering message sync:', error);
        const fragment = document.createDocumentFragment();
        const textNode = document.createTextNode(text);
        fragment.appendChild(textNode);
        return fragment;
    }
}

export function updateStreamingContent(container: HTMLElement, newContent: string): void {
    let streamingNode = container.querySelector('.streaming-content') as HTMLElement;
    
    if (!streamingNode) {
        streamingNode = document.createElement('div');
        streamingNode.className = 'streaming-content';
        container.appendChild(streamingNode);
    }
    
    const html = md.render(newContent);
    streamingNode.innerHTML = DOMPurify.sanitize(html);
    
    applyHighlightingToContainer(streamingNode);
}

export function finalizeStreamingMessage(container: HTMLElement): void {
    const streamingNode = container.querySelector('.streaming-content');
    if (streamingNode) {
        streamingNode.classList.remove('streaming-content');
    }
}

export function clearCache(): void {
    renderCache.clear();
}

export function getCacheSize(): number {
    return renderCache.size;
}